import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LifeBuoy, Loader2, AlertTriangle, MessageSquareHeart, HelpCircle, Zap } from "lucide-react";
import { useI18n } from "@/i18n";

const KINDS = [
  { id: "problem", tk: "problem", icon: AlertTriangle, color: "text-signal-error border-signal-error/40 bg-signal-error/10" },
  { id: "feedback", tk: "feedback", icon: MessageSquareHeart, color: "text-aurora-cyan border-aurora-cyan/40 bg-aurora-cyan/10" },
  { id: "other", tk: "other", icon: HelpCircle, color: "text-aurora-purple border-aurora-purple/40 bg-aurora-purple/10" },
];

export const TicketDialog = ({ orderId = null, vehicle = "", trigger = null, onCreated }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("problem");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [ent, setEnt] = useState(null);
  const [boost, setBoost] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBoost(false);
    api.get("/support/entitlement").then(({ data }) => setEnt(data)).catch(() => setEnt(null));
  }, [open]);

  const canBoost = ent && !ent.express && ent.boost_credits > 0;

  const submit = async () => {
    if (!subject.trim() || !message.trim()) { toast.error(t("tdialog.required")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/tickets", { order_id: orderId, subject, message, kind, boost_express: canBoost && boost });
      toast.success(t("tdialog.created", { ref: data.ref }));
      setOpen(false); setSubject(""); setMessage(""); setKind("problem"); setBoost(false);
      onCreated?.(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 402) { toast.error(t("tdialog.boostInsufficient")); }
      else { toast.error((typeof d === "string" ? d : null) || t("tdialog.error")); }
    }
    setBusy(false);
  };

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger, { onClick: () => setOpen(true) })
      ) : (
        <button data-testid="open-ticket-btn" onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 border border-signal-error/30 text-signal-error rounded-sm font-display font-bold uppercase tracking-wider text-xs hover:bg-signal-error/10 transition-colors">
          <LifeBuoy className="w-4 h-4" /> {t("tdialog.report")}
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ticket-dialog" className="max-w-lg popover-solid border border-white/10">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <LifeBuoy className="w-5 h-5 text-aurora-cyan" /> {t("tdialog.title")}
            </DialogTitle>
          </DialogHeader>
          {vehicle && <p className="font-mono text-[10px] text-zinc-500 -mt-2">{t("tdialog.order")} {vehicle}</p>}
          <div className="flex gap-2 mt-2">
            {KINDS.map((k) => {
              const on = kind === k.id; const Icon = k.icon;
              return (
                <button key={k.id} data-testid={`ticket-kind-${k.id}`} onClick={() => setKind(k.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-sm border font-mono text-[10px] uppercase tracking-widest transition-colors ${on ? k.color : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
                  <Icon className="w-3.5 h-3.5" /> {t(`tdialog.${k.tk}`)}
                </button>
              );
            })}
          </div>
          <input data-testid="ticket-subject" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t("tdialog.subjectPh")}
            className="w-full mt-3 bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
          <textarea data-testid="ticket-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("tdialog.messagePh")}
            className="w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none resize-none" />

          {ent?.express && (
            <div data-testid="support-express-included" className="flex items-center gap-2 px-3 py-2 rounded-sm border border-aurora-cyan/40 bg-aurora-cyan/10 text-aurora-cyan font-mono text-[11px] uppercase tracking-widest">
              <Zap className="w-4 h-4 shrink-0" /> {t("tdialog.expressIncluded")}
            </div>
          )}
          {canBoost && (
            <button
              type="button"
              data-testid="ticket-boost-toggle"
              onClick={() => setBoost((b) => !b)}
              className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-sm border transition-colors ${boost ? "border-aurora-cyan bg-aurora-cyan/10" : "border-white/10 hover:border-white/30"}`}
            >
              <span className={`mt-0.5 w-4 h-4 rounded-sm border grid place-items-center shrink-0 ${boost ? "bg-aurora-cyan border-aurora-cyan" : "border-white/30"}`}>
                {boost && <Zap className="w-3 h-3 text-black" />}
              </span>
              <span className="min-w-0">
                <span className="block font-display text-sm font-bold uppercase tracking-tight text-white">
                  {t("tdialog.boost", { n: ent.boost_credits })}
                </span>
                <span className="block font-mono text-[10px] text-zinc-500 mt-0.5">
                  {t("tdialog.boostNote", { eur: Math.round(ent.vip_threshold_eur), bal: ent.credits })}
                </span>
              </span>
            </button>
          )}

          <button data-testid="submit-ticket-btn" onClick={submit} disabled={busy}
            className="w-full bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider py-3 rounded-sm flex items-center justify-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LifeBuoy className="w-4 h-4" />} {t("tdialog.send")}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
};
